import dayModeEventsRowComponentUrl from './dayModeEventsRowComponent.html';
const OFFSET_TOP = 8;
const PERCENTAGE_MARGIN = 0.25;

class DayModeEventsRowComponent {
  constructor(
    Authentication,
    EventCollection,
    Restangular,
    ShiftUtils,
    ShiftHeaderUtils,
    ShiftAcceptanceUtils,
    Utils,
    formatDate,
    toTimeShortFilter,
    orderByFilter,
    sdk
  ) {
    'ngInject';

    Object.assign(this, {
      Authentication,
      EventCollection,
      Restangular,
      ShiftUtils,
      ShiftHeaderUtils,
      ShiftAcceptanceUtils,
      Utils,
      formatDate,
      toTimeShortFilter,
      orderByFilter,
      sdk,
    });
  }

  $onInit() {
    this.EVENT_STATUS = this.sdk('constants').EVENT_STATUS;
    this.getShiftDisplayLabels = this.sdk('model-extensions/getShiftDisplayLabels');
    this.hourBoundaries = this._getHourBoundaries();
  }

  $onChanges({ currentDays, date }) {
    this.hourBoundaries = this._getHourBoundaries();

    if (date) {
      const { visibleHoursStart, visibleHoursEnd } = this.Authentication.user.orgSettings;
      this.businessDay = this.Utils.getBusinessDayForCalendar(
        visibleHoursStart,
        visibleHoursEnd,
        this.date
      );
    }

    if (currentDays) {
      delete this._events;
    }
  }

  shouldShowAcceptanceStatus(event) {
    return this.ShiftAcceptanceUtils.shouldShowAcceptanceStatus(event);
  }

  get events() {
    if (!this._events || this._previousUserEvents !== this.user.events) {
      this._events = this.currentDays.reduce((events, date) => {
        if (Array.isArray(this.user.events[date])) {
          const _userEvents = this.user.events[date].filter(
            (event) =>
              !events.some((e) => e.id === event.id) &&
              this.isWithinBusinessDay(event, this.businessDay)
          );
          return [...events, ..._userEvents];
        }
        return events;
      }, []);
      this._events = this.orderByFilter(this._events, [
        'sort',
        'positionNameSort',
        'positionName',
        'dtstart',
      ]);
    }
    this._previousUserEvents = this.user.events;
    return this._events;
  }

  isWithinBusinessDay({ dtstart, dtend, openEnd }, { visibleStart, visibleEnd }) {
    const startTime = dtstart.getTime();
    const endTime = !openEnd ? dtend.getTime() : this.Utils.addHours(dtend, 4).getTime();
    const visibleStartTime = visibleStart.getTime();
    const visibleEndTime = visibleEnd.getTime();
    const isFullDayVisible = (visibleEndTime - visibleStartTime) / 60 / 60 / 1000 === 24;

    if (isFullDayVisible) {
      return true;
    }

    if (startTime >= visibleStartTime) {
      return startTime < visibleEndTime || endTime <= visibleEndTime;
    }

    return endTime >= visibleEndTime || endTime > visibleStartTime;
  }

  isPast(day) {
    return this.ShiftUtils.isPastDay(day);
  }

  getStyle(currentEvent) {
    const visibleHours = this.ShiftHeaderUtils.getProperHours();
    const numberOfVisibleHours = visibleHours.length;
    const hourPercentage = 100 / numberOfVisibleHours;
    const { left, width } = currentEvent;
    const top = isPartialAvailability(currentEvent)
      ? 0
      : this.ShiftUtils.getShiftBoxHeight({ mode: 'day' }) * currentEvent.row;
    const actualWidth = width * hourPercentage - 2 * PERCENTAGE_MARGIN;
    const isLengthTooShort = actualWidth < 0.2;

    return {
      top: `${OFFSET_TOP + top}px`,
      left: `${left * hourPercentage + PERCENTAGE_MARGIN}%`,
      width: isLengthTooShort ? '0.2%' : `${actualWidth}%`,
      display: width ? 'block' : 'none',
    };
  }

  previewChangeDuration(hour, minute, boundary, event) {
    const tempDate = new Date((boundary === 'left' ? event.dtstart : event.dtend).getTime());
    tempDate.setHours(hour);
    tempDate.setMinutes(minute);

    let tempTimeLabel;

    if (boundary === 'left') {
      tempTimeLabel = this.getShiftDisplayLabels({ dtstart: tempDate, dtend: event.dtend }).label;
    } else if (boundary === 'right') {
      tempTimeLabel = this.getShiftDisplayLabels({ dtstart: event.dtstart, dtend: tempDate }).label;
    }

    if (event.shiftAppearance.day.timeSource) {
      const [line, index] = event.shiftAppearance.day.timeSource;
      event._tempLines = { ...event.shiftAppearance.day };
      const lineParts = event._tempLines[line].split(' • ');
      lineParts[index] = tempTimeLabel;
      event._tempLines[line] = lineParts.join(' • ');
    }
  }

  _changeLeftBoundaryDuration(shift, hour, minute) {
    const prevHour = shift.dtstart.getHours();
    const prevMinute = shift.dtstart.getMinutes();
    let isChanged = false;
    if (hour !== prevHour || minute !== prevMinute) {
      isChanged = true;

      const diffMinutes = hour * 60 + minute - prevHour * 60 - prevMinute;
      shift.dtstart = this.Utils.addHours(shift.dtstart, diffMinutes / 60);

      if (shift.dtend.getTime() - shift.dtstart.getTime() > 1000 * 3600 * 24) {
        shift.dtstart = this.Utils.addDays(shift.dtstart, 1);
      }

      if (shift.dtend.getTime() - shift.dtstart.getTime() < 0) {
        shift.dtstart = this.Utils.addDays(shift.dtstart, -1);
      }
    }

    return isChanged;
  }

  _changeRightBoundaryDuration(shift, hour, minute) {
    const prevHour = shift.dtend.getHours();
    const prevMinute = shift.dtend.getMinutes();
    let isChanged = false;

    if (hour !== prevHour || minute !== prevMinute) {
      isChanged = true;

      const diffMinutes = hour * 60 + minute - prevHour * 60 - prevMinute;
      shift.dtend = this.Utils.addHours(shift.dtend, diffMinutes / 60);

      if (shift.dtend.getTime() - shift.dtstart.getTime() > 1000 * 3600 * 24) {
        shift.dtend = this.Utils.addDays(shift.dtend, -1);
      }

      if (shift.dtend.getTime() - shift.dtstart.getTime() < 0) {
        shift.dtend = this.Utils.addDays(shift.dtend, 1);
      }
    }

    return isChanged;
  }

  async changeDuration(hour, minute, boundary, shiftId, event) {
    delete event._tempLines;
    const shiftFromCollection = this.EventCollection.allEvents.find((e) => e.id === shiftId);
    let isChanged = false;
    const originalShift = { ...shiftFromCollection };
    const shift = this.Restangular.restangularizeElement(null, shiftFromCollection, 'calendar');

    await this.ShiftUtils.maybeWarnAboutUnpublishing(shift, { onlyTime: true });

    const rollbackChanges = () => {
      Object.assign(shiftFromCollection, originalShift);
      this.trigger();
    };

    if (boundary === 'left') {
      isChanged = this._changeLeftBoundaryDuration(shift, hour, minute);
    }

    if (boundary === 'right' || shift.openEnd) {
      isChanged = this._changeRightBoundaryDuration(shift, hour, minute);
    }

    if (isChanged) {
      shiftFromCollection.status = shift.status = this.EVENT_STATUS.PLANNING;
      shiftFromCollection.dtstart = shift.getDtstartString();
      shiftFromCollection.dtend = shift.getDtendString();

      this.trigger();
      this.submitChangedDuration({ $shift: shift, $rollbackChanges: rollbackChanges });
    }
  }

  getResizableShiftConfig(shift) {
    const isResizableShift =
      this.group.key !== 'available' && shift.type === 'shift' && !shift.openEnd;
    const isEnabled = isResizableShift && this.Authentication.user.isManager;

    return {
      left: isEnabled,
      right: isEnabled,
    };
  }

  _getHourBoundaries() {
    const leftDay = this.currentDays[0];
    const leftHour = this.Utils.getFullHour(this.hours[0]);
    const rightDay = this.currentDays.slice(-1)[0];
    const rightHour = this.Utils.getFullHour(this.hours.slice(-1)[0]);

    return [
      this.Utils.getRealDate(`${leftDay} ${leftHour}`),
      this.Utils.addHours(this.Utils.getRealDate(`${rightDay} ${rightHour}`), 1),
    ];
  }
}

export default {
  bindings: {
    user: '<',
    group: '<',
    dateString: '<',
    currentDays: '<',
    hours: '<',
    date: '<',
    selectedEvents: '<',
    recentlyCreatedEventId: '<', // for education
    selectionChanged: '&',
    submitChangedDuration: '&',
    openShift: '&',
    trigger: '&',
  },
  templateUrl: dayModeEventsRowComponentUrl,
  controller: DayModeEventsRowComponent,
  controllerAs: 'ctrl',
};

function isPartialAvailability(event) {
  return event.type === 'availability' && !event.fullDay;
}
